/**
 * S15 — Compliance Centre (`/compliance`).
 *
 * Investor-friendly framework view. KPI strip + framework coverage cards
 * (clickable into drawer) + evidence locker. Customer Mode renders an
 * empty state pointing the user at agent on-boarding.
 *
 * Read-only: report builder, scheduled exports and policy authoring
 * are deferred to later waves.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, FileSignature, AlertTriangle, MapPin, ArrowUpRight, CalendarClock, Plus,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { CoverageRing, GapBadge, Sparkline, TrendCaption } from "./components/CompliancePrimitives";
import FrameworkDetailDrawer from "./FrameworkDetailDrawer";

export default function ComplianceCentre() {
  const { user } = useAuth();
  // Phase 3b — frameworks come from REST; policies/agents/reports
  // remain on the bundle (used here only for cross-counts on the
  // compliance summary cards).
  const { items: frameworks, loading, error, lastSyncedAt, refetch } = useEntityList("/api/frameworks");
  const { policies, agents, reports, isCustomer } = useTenantData();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const summary = useMemo(() => deriveSummary(frameworks, reports), [frameworks, reports]);

  const openFramework = useCallback((f) => setSelected(f), []);
  const closeDrawer = useCallback(() => setSelected(null), []);
  // Always pull the latest enriched record so the drawer reflects mutations.
  const liveFramework = selected
    ? frameworks.find((f) => f.id === selected.id) || selected
    : null;

  if (loading && frameworks.length === 0) return <CCSkeleton />;
  if (error) return <CCError message={error.message} />;
  if (isCustomer && frameworks.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmit={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="compliance-centre">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} syncing={loading} onRefetch={refetch} />
      <KpiStrip summary={summary} />

      <h2 className="mt-8 text-h2 text-ink-primary">Frameworks</h2>
      <p className="text-ink-secondary mt-1 max-w-3xl">
        Coverage and evidence across every framework ALGOS enforces for this tenant.
        Click any card to open the full control map, gap register and recent evidence.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="framework-grid">
        {frameworks.map((f) => (
          <FrameworkCard key={f.id} framework={f} onSelect={openFramework} />
        ))}
      </div>

      <EvidenceLocker reports={reports} />

      <FrameworkDetailDrawer
        framework={liveFramework}
        onClose={closeDrawer}
        policies={policies}
        agents={agents}
        reports={reports}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header({ lastSyncedAt, syncing, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Compliance · Centre</p>
        <h1 className="text-display-md text-ink-primary mt-1">Compliance Centre</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          A live view of how well every governed AI workflow maps to the
          regulatory and ethical frameworks that matter to this tenant —
          with signed evidence ready for export at any moment.
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={syncing}
        onRefetch={onRefetch}
        testId="compliance-centre-synced"
      />
    </div>
  );
}

function CCSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="compliance-centre-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[380px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function CCError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="compliance-centre-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Compliance · Centre</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load compliance data</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

function KpiStrip({ summary }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="compliance-kpis">
      <KpiTile
        label="Frameworks tracked"
        value={summary.frameworkCount.toString()}
        delta={`${summary.fullyCovered} at 100%`}
        deltaTone={summary.fullyCovered > 0 ? "up" : "neutral"}
        hint="across all jurisdictions"
        icon={ShieldCheck}
        tone="tralenex"
        testId="compliance-kpi-frameworks"
      />
      <KpiTile
        label="Average coverage"
        value={`${summary.avgCoverage}%`}
        delta={summary.avgCoverage >= 90 ? "Board-ready" : "Trending up"}
        deltaTone={summary.avgCoverage >= 90 ? "up" : "neutral"}
        hint="weighted across mapped controls"
        icon={ShieldCheck}
        tone="gold"
        testId="compliance-kpi-coverage"
      />
      <KpiTile
        label="Open gaps"
        value={summary.openGaps.toString()}
        delta={summary.openGaps === 0 ? "All evidenced" : "Action required"}
        deltaTone={summary.openGaps === 0 ? "up" : "down"}
        hint="missing or partial evidence"
        icon={AlertTriangle}
        tone={summary.openGaps === 0 ? "tralenex" : "risk"}
        testId="compliance-kpi-gaps"
      />
      <KpiTile
        label="Signed evidence"
        value={summary.signedReports.toString()}
        delta="ready for export"
        deltaTone="up"
        hint="reports generated · signed"
        icon={FileSignature}
        tone="opp"
        testId="compliance-kpi-evidence"
      />
    </div>
  );
}

function FrameworkCard({ framework: f, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(f)}
      data-testid={`framework-card-${f.id}`}
      className="text-left surface-card shadow-e1 hover:shadow-e2 transition-shadow p-5 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow text-ink-secondary">{f.id}</p>
          <h3 className="text-h3 text-ink-primary mt-0.5 truncate">{f.name}</h3>
          <p className="mt-1 text-meta text-ink-secondary inline-flex items-center gap-1">
            <MapPin size={11} /> {f.jurisdiction}
          </p>
        </div>
        <CoverageRing value={f.coverage} size={84} stroke={6} />
      </div>

      <div className="grid grid-cols-3 gap-1.5 text-meta">
        <Stat label="Implemented" value={f.controlsMet} tone="text-risk-low" />
        <Stat label="Partial"     value={f.controlsPartial} tone="text-risk-med" />
        <Stat label="Gap"         value={f.controlsGap} tone="text-risk-high" />
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="min-w-0">
          <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">
            Coverage · last 12 weeks
          </p>
          <TrendCaption data={f.coverageHistory} />
        </div>
        <Sparkline
          data={f.coverageHistory}
          testId={`framework-sparkline-${f.id}`}
        />
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border-subtle/60">
        <span className="text-meta text-ink-secondary inline-flex items-center gap-1">
          <CalendarClock size={11} /> {f.lastEvidenceTs}
        </span>
        <GapBadge count={(f.gaps || []).length} />
      </div>

      <span className="self-end text-meta text-tralenex-700 inline-flex items-center gap-1 font-medium">
        Open framework <ArrowUpRight size={12} />
      </span>
    </button>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-md bg-inset/50 px-2 py-1.5">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold tabular-nums leading-none ${tone}`}>{value}</p>
    </div>
  );
}

function EvidenceLocker({ reports }) {
  return (
    <section className="mt-10" data-testid="evidence-locker">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-h2 text-ink-primary">Evidence locker</h2>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Every ALGOS-signed report is hash-chained into the audit ledger and
            available for download or scheduled export.
          </p>
        </div>
        <span className="text-meta text-ink-secondary tabular-nums">
          {reports.length} signed
        </span>
      </div>
      {reports.length === 0 ? (
        <p className="mt-4 text-meta text-ink-secondary" data-testid="evidence-empty">
          No evidence reports generated yet.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-border-subtle/60 surface-card shadow-e1 overflow-hidden">
          {reports.map((r) => (
            <li
              key={r.id}
              className="px-4 py-3 flex items-center gap-3"
              data-testid={`evidence-row-${r.id}`}
            >
              <FileSignature size={14} className="text-tralenex-700 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink-primary truncate">{r.name}</p>
                <p className="text-meta text-ink-secondary">
                  {r.generatedAt} · {r.generatedBy} · {r.sizeMb} MB · signed
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center px-2 py-1 rounded bg-tralenex-50 text-[11px] text-tralenex-900 font-medium shrink-0">
                {r.framework}
              </span>
              <span className="text-meta font-mono text-ink-disabled tabular-nums shrink-0">{r.id}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CustomerEmpty({ userName, onSubmit }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="compliance-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Compliance · Centre</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No frameworks tracked yet. ALGOS ships with baseline mappings for
          EU AI Act, NIST AI RMF, ISO 42001, APRA CPS 234, ASIC RG 271,
          Privacy Act AU and AU AI Ethics. Connect your agents in the Inventory
          and we will instantiate the right framework set for your jurisdiction.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="compliance-empty-cta"
        >
          <Plus size={14} /> Set up agent inventory
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function deriveSummary(frameworks, reports) {
  const frameworkCount = frameworks.length;
  const signedReports = (reports || []).filter((r) => r.signed).length;
  if (frameworkCount === 0) {
    return { frameworkCount: 0, avgCoverage: 0, openGaps: 0, fullyCovered: 0, signedReports };
  }
  const avgCoverage = Math.round(
    frameworks.reduce((s, f) => s + f.coverage, 0) / frameworkCount,
  );
  const openGaps = frameworks.reduce((s, f) => s + (f.gaps?.length || 0), 0);
  const fullyCovered = frameworks.filter((f) => f.coverage === 100).length;
  return { frameworkCount, avgCoverage, openGaps, fullyCovered, signedReports };
}
